import React from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import FormControl from '@mui/material/FormControl';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { Controller, useForm } from "react-hook-form";
import "../i18n";
import { useTranslation } from 'react-i18next';
import TopCountryCodes from '../utils/TopCountryCodes';
import CountryCodes from '../utils/CountryCodes';
import Nationalities from '../utils/Nationalities';
import Languages from '../utils/Languages';
import { Box } from '@mui/material';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import dayjs from 'dayjs';
import PrivacyPolicy from './privacy-policy';


export default function EnrollmentForm(props) {
    const { t, i18n } = useTranslation();
    const public_URL = process.env.PUBLIC_URL;
    
    // Shared styles for Terms & Conditions
    const tcStyles = {
        clause: {
            fontSize: '1rem',
            lineHeight: '1.5',
            marginBottom: '12px'
        },
        sectionHeader: {
            fontSize: window.innerWidth < 600 ? '15px' : '20px',
            fontWeight: 'bold',
            margin: '16px 0 8px 0',
            color: '#005157'
        },
        bulletList: {
            marginLeft: window.innerWidth < 600 ? '15px' : '30px',
            marginBottom: '12px',
            paddingLeft: '8px'
        },
        bulletItem: {
            marginBottom: '4px',
            fontSize: '1rem',
            lineHeight: '1.5'
        }
    };

    // Terms & Conditions structure for cleaner rendering
    const termsStructure = [
        { type: 'clauses', items: ['C1', 'C2', 'C3', 'C4', 'C5'] },
        { type: 'header', key: 'P2' },
        { type: 'clauses', items: ['C6', 'C7', 'C8', 'C9', 'C10', 'C11', 'C12', 'C13'] },
        { type: 'header', key: 'P3' },
        { type: 'clauses', items: ['C14', 'C15', 'C16', 'C17'] },
        { type: 'header', key: 'P4' },
        { type: 'clauses', items: ['C18', 'C19'] },
        { type: 'bullets', key: 'C19_bullets' },
        { type: 'clauses', items: ['C20', 'C21', 'C22', 'C23', 'C24', 'C25', 'C26', 'C27'] },
        { type: 'bullets', key: 'C27_contact' },
        { type: 'clauses', items: ['C28', 'C29'] }
    ];

    // Render Terms & Conditions content
    const renderTermsContent = () => {
        return termsStructure.map((section, index) => {
            switch (section.type) {
                case 'clauses':
                    return section.items.map((clause, clauseIndex) => (
                        <Typography key={`${clause}-${clauseIndex}`} gutterBottom style={tcStyles.clause}>
                            {t(clause)}
                        </Typography>
                    ));
                case 'header':
                    return (
                        <Typography key={`header-${index}`} style={tcStyles.sectionHeader}>
                            {t(section.key)}
                        </Typography>
                    );
                case 'bullets':
                    return (
                        <ul key={`bullets-${index}`} style={tcStyles.bulletList}>
                            {t(section.key, { returnObjects: true }).map((item, bulletIndex) => (
                                <li key={`${section.key}-${bulletIndex}`} style={tcStyles.bulletItem}>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    );
                default:
                    return null;
            }
        });
    };

    const languageList = [{ languageID: 41, abbrName: 'en', nativeName: 'English', nationalityISO:"GB", imgsrc: public_URL + "/images/EN.png" },
    { languageID: 33, abbrName: 'zh', nativeName: '中文',  nationalityISO:"CN", imgsrc: public_URL + "/images/CN.png" },
    { languageID: 127, abbrName: 'vn', nativeName: 'Tiếng Việt',  nationalityISO:"VN", imgsrc: public_URL + "/images/VN.png" },
    { languageID: 77, abbrName: 'kr', nativeName: '한국어',  nationalityISO:"KR", imgsrc: public_URL + "/images/KR.png" }];
    const [language, setLanguage] = React.useState("en");
    const [mediumFont, setMediumFont] = React.useState("MontserratMedium");
    const [boldFont, setBoldFont] = React.useState("MontserratBold");
    const [agreeChecked, setAgreeChecked] = React.useState(false);
    const [declarationChecked, setDeclarationChecked] = React.useState(false);
    const [declarationOpen, setDeclarationOpen] = React.useState(false);
    const [marketingOptOut, setMarketingOptOut] = React.useState(false);
    const [privacyOpen, setPrivacyOpen] = React.useState(false);
    const [privacyChecked, setPrivacyChecked] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    
    const [defaultNationality, setDefaultNationality]=React.useState("GB");
    const [defaultLanguage, setDefaultLanguage]=React.useState("51");

    var countryCodes = sortByKey(CountryCodes, 'code');
    var topCountryCodes = sortByKey(TopCountryCodes, 'priority');
    var countryCodeList = JSON.parse(JSON.stringify(countryCodes));
    var topcountryCodeList = JSON.parse(JSON.stringify(topCountryCodes));

    var nationalies=sortByKey(Nationalities,'CountryDescription');
    var nationalityList = JSON.parse(JSON.stringify(nationalies));

    var languages=sortByKey(Languages,'id');
    var preferLanguageList = JSON.parse(JSON.stringify(languages));


    React.useEffect(() => {        
        setLanguage(language);
        i18n.changeLanguage(language);
        setAgreeChecked(false);       
        
    }, [])  // eslint-disable-line react-hooks/exhaustive-deps

    function getMaxYear() {
        var date = new Date();
        var thisyear = date.getFullYear() - 15
        var thismonth = date.getMonth() + 1;
        var thisday = date.getDate();
        return (thismonth + '/' + thisday + '/' + thisyear).toString();
    }

    function sortByKey(array, key) {
        return array.sort(function (a, b) {
            var x = a[key]; var y = b[key];
            return ((x < y) ? -1 : ((x > y) ? 1 : 0));
        });
    }
    const handleChangeLanguage = (event) => {
        setLanguage(event.target.value);
        i18n.changeLanguage(event.target.value);
        var languagestring = event.target.value;
        if (languagestring === 'zh') {
            setMediumFont('NotoSansSCMedium');
            setBoldFont('NotoSansSCBold');            
        }
        else if (languagestring === 'kr') {
            setMediumFont('NotoSansKRMedium');
            setBoldFont('NotoSansKRBold');            
        }
        else if (languagestring === 'en' || languagestring === 'vn') {
            setMediumFont('MontserratMedium');
            setBoldFont('MontserratBold');            
        }    
        var findlanguage=languageList.find(el=>el.abbrName===languagestring);        
       
        setDefaultNationality(findlanguage.nationalityISO); 
        setDefaultLanguage(findlanguage.languageID);      
    }

    function convertUTCDateToLocalDate(date) {
        var newDate = new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
        return newDate;
    }

    const { handleSubmit, control, formState: { errors } } = useForm(
        {
            defaultValues: {
                birthday: null
            }
           
        }        
    );

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setAgreeChecked(true);
        setOpen(false);
    };

    const handleDisagreeClose = () => {
        setAgreeChecked(false);
        setOpen(false);
    } 

    const handleOnSubmit = (data) => {
        if (!agreeChecked || !privacyChecked || !declarationChecked) return;
        console.log(data)
        let d = data.birthday.$d;
        console.log(d.toISOString())

        let nd = convertUTCDateToLocalDate(d);
        console.log(nd.toISOString())
        var strCountryCode = data.countryCode;
        var countryDialCode = "";
        if (strCountryCode.length >= 0) {
            countryDialCode = strCountryCode.substring(3, strCountryCode.length);
        }

        console.log('countryDialCode:' + countryDialCode);
        const info = {
            IdType: data.idType,
            Title: data.title,
            Id: data.id,
            LastName: data.lastName,
            FirstName: data.firstName,
            Gender: data.gender,
            Birthday: nd,
            CountryCode: countryDialCode,
            PhoneNo: data.phoneNo,
            Email: data.email,
            Lang: language,
            LanguageId: data.preferLanguage,
            Nationality: data.nationality,
            MarketingOptOut: marketingOptOut
        }
        if (props.onSubmit) {
            props.onSubmit(info);
            setAgreeChecked(false);
            setDeclarationChecked(false);
            setPrivacyChecked(false);
        }
    }


    return (
        <form onSubmit={handleSubmit(handleOnSubmit)}>
            <div class="div">
                <div class="div-img">
                    {/* Logo - minimal spacing */}
                    <div class="logo-container">
                        <img
                            src={public_URL + "/images/HOIANAREWARDSLOGO.svg"}
                            class="img"
                            alt=""
                            loading="lazy"
                        />
                    </div>
                    
                    {/* Welcome text - compact */}
                    <div class="header-welcome-section">
                        <div class={"header-welcome-text " + boldFont}>{t("Welcome1")}</div>
                        <div class={"header-welcome-text " + boldFont}>{t("Welcome2")}</div>
                    </div>
                    
                    {/* Language selector - minimal size */}
                    <div class="language-selector-container">
                        <Select 
                            value={language} 
                            size="small"
                            sx={{ 
                                backgroundColor: "rgba(255,255,255,0.15)",
                                color: "white",
                                borderRadius: "4px",
                                border: "none",
                                "& .MuiOutlinedInput-root": { 
                                    "& > fieldset": { border: 'none' },
                                    padding: "2px 8px"
                                },
                                "& .MuiSelect-icon": { color: "white" },
                                "& .MuiSelect-select": {
                                    padding: "4px 8px",
                                    fontSize: "13px"
                                },
                                fontSize: "13px",
                                height: "32px",
                                minWidth: "100px"
                            }}
                            onChange={handleChangeLanguage}>
                            {languageList.map(item => (
                                <MenuItem value={item.abbrName} key={item.abbrName} sx={{ fontSize: "13px", padding: "4px 8px" }}>
                                    <img alt="" src={item.imgsrc} style={{width: '16px', marginRight: '6px'}} />
                                    {item.nativeName}
                                </MenuItem>
                            ))}
                        </Select>
                    </div>
                </div>
                <div class="divmargin">
                <div class="divmargin">
                    <Controller
                        control={control}
                        name="title"
                        rules={{ required: t("Title") + t("IsRequired") }}
                        render={({ field }) => (
                            <FormControl sx={{ m: 0.5, minWidth: 320 }} error={!!errors.title}  >
                                <label class={"label " + boldFont} >{t("Title")}</label>
                                <select
                                    class={"select fontstyle " + mediumFont}
                                    {...field}
                                    label="Title"                              >
                                    <option value="">
                                        <em>{t("None")}</em>
                                    </option>
                                    <option value={'MR'}>{t("MR")}</option>
                                    <option value={'MRS'}>{t("MRS")}</option>
                                    <option value={'MS'}>{t("MS")}</option>
                                    <option value={'MISS'}>{t("MISS")}</option>
                                </select>
                                <panel class={"panel " + mediumFont}>{errors.title && `${errors.title.message}`}</panel>
                            </FormControl>)}
                    />
                </div>
                <div class="divmargin">
                    <Controller
                        control={control}
                        name="firstName"
                        rules={{
                            required: t("FirstName2") + t("IsRequired"),
                            pattern: {
                                value: /(^[a-zA-Z][a-zA-Z\s]{0,30}[a-zA-Z]$)/,
                                message: t("FirstName2") + t("InValid")
                            }
                        }}
                        render={({ field }) => (
                            <FormControl sx={{ m: 0.5, minWidth: 320 }} error={!!errors.firstName}  >
                                <label class={"label " + boldFont}>{t("FirstName")}</label>
                                <input
                                    class={"input fontstyle " + mediumFont}
                                    {...field}
                                    label={t("FirstName")}
                                    error={!!errors.firstName}
                                    helperText={errors.firstName && `${errors.firstName.message}`}
                                />
                                <panel class={"panel " + mediumFont}>{errors.firstName && `${errors.firstName.message}`}</panel>
                            </FormControl>)}
                    />
                </div>
                <div class="divmargin">
                    <Controller
                        control={control}
                        name="lastName"
                        rules={{
                            required: t("LastName2") + t("IsRequired"),
                            pattern: {
                                value: /(^[a-zA-Z][a-zA-Z\s]{0,30}[a-zA-Z]$)/,
                                message: t("LastName2") + t("InValid")
                            }
                        }}
                        render={({ field }) => (
                            <FormControl sx={{ m: 0.5, minWidth: 320 }} error={!!errors.lastName}  >
                                <label class={"label " + boldFont} >{t("LastName")}</label>
                                <input
                                    class={"input fontstyle " + mediumFont}
                                    {...field}
                                    label={t("LastName")}
                                    error={!!errors.lastName}
                                    helperText={errors.lastName && `${errors.lastName.message}`}
                                />
                                <panel class={"panel " + mediumFont}>{errors.lastName && `${errors.lastName.message}`}</panel>
                            </FormControl>)}
                    />
                </div>
                <div class="divmargin">
                    <Controller
                        control={control}
                        name="gender"
                        rules={{ required: t("Gender") + t("IsRequired") }}
                        render={({ field }) => (
                            <FormControl sx={{ m: 0.5, minWidth: 150 }} error={!!errors.gender}>
                                <label class={"label " + boldFont}>{t("Gender")}</label>
                                <select
                                    class={"select fontstyle narrow " + mediumFont}
                                    {...field} >
                                    <option value="">
                                        <em>{t("None")}</em>
                                    </option>                                    
                                    <option value={'M'}>{t("Male")} </option>
                                    <option value={'F'}>{t("Female")} </option>
                                </select>
                                <panel class={"panel " + mediumFont}>{errors.gender && `${errors.gender.message}`}</panel>
                            </FormControl>
                        )}
                    />
                    <Controller
                        control={control}
                        name="birthday"
                        rules={{
                            required: t("BOD") + t("IsRequired"),
                        }}
                        render={({ field }) => (
                            <FormControl sx={{ m: 0.5, minWidth: 100 }} error={!!errors.birthday}>
                                <label class={"label " + boldFont} > {t("Birthday")}</label>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <MobileDatePicker {...field}
                                        displayStaticWrapperAs="mobile"
                                        value={field.value}
                                        disableFuture={true}  
                                        showToolbar={false}
                                        maxDate={dayjs(getMaxYear())}
                                        defaultCalendarMonth={dayjs(getMaxYear())}
                                        views={['year', 'month', 'day']} 
                                        renderInput={({ inputRef, inputProps, InputProps }) => (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <input class={"input fontstyle narrow " + mediumFont}   onKeyDown={(e) => { e.preventDefault(); }}
                                                    ref={inputRef} {...inputProps} />
                                                {InputProps?.endAdornment}
                                            </Box>)}
                                    />
                                </LocalizationProvider>                               
                                <panel class={"panel " + mediumFont}>{errors.birthday && `${errors.birthday.message}`}</panel>
                            </FormControl>
                        )}
                    />

                </div>
                <div class="divmargin">
                    <Controller
                        control={control}
                        name="idType"
                        //rules={{ required: t("IDType") + t("IsRequired") }}
                        defaultValue= "PP"
                        render={({ field }) => (
                            <FormControl sx={{ m: 0.5, minWidth: 150 }} error={!!errors.idType}>
                                <label class={"label " + boldFont} >{t("IDType")} </label>
                                <select {...field} class={"select fontstyle narrow " + mediumFont} defaultValue="PP" >                                     
                                    <option value={'NI'}>{t("NationalID")}</option>
                                    <option value={'PP'}  selected>{t("Passport")}</option>
                                </select>
                                <panel class={"panel " + mediumFont}>{errors.idType && `${errors.idType.message}`}</panel>
                            </FormControl>
                        )}
                    />
                    <Controller
                        control={control}
                        name="id"
                        rules={{ required: t("ID") + t("IsRequired") }}
                        render={({ field }) => (
                            <FormControl sx={{ m: 0.5, minWidth: 100 }} error={!!errors.id}  >
                                <label class={"label " + boldFont}>{t("ID")} </label>
                                <input
                                    class={"input fontstyle narrow " + mediumFont}
                                    {...field}
                                    label={t("ID")}
                                    error={!!errors.id}
                                    helperText={errors.id && `${errors.id.message}`}
                                />
                                <panel class={"panel " + mediumFont}>{errors.id && `${errors.id.message}`}</panel>
                            </FormControl>)}
                    />
                </div>
                <div class="divmargin">
                    <Controller
                        control={control}
                        name="countryCode"
                        //rules={{ required: t("Code") + t("IsRequired") }}
                        defaultValue= "CN+86"
                        render={({ field }) => (
                            <FormControl sx={{ m: 0.5, minWidth: 150 }} error={!!errors.countryCode}>
                                <label class={"label " + boldFont}>{t("Phone")} </label>
                                <select {...field} class={"select fontstyle narrow " + mediumFont} defaultValue='CN+86' >                                    
                                    {topcountryCodeList.map(item => (
                                        <option value={item.code + item.dial_code} >{item.code} {item.dial_code} {item.name}</option >
                                    ))}
                                    {countryCodeList.map(item => (
                                        <option value={item.code + item.dial_code} >{item.code} {item.dial_code} {item.name}</option >
                                    ))}
                                </select>
                                <panel class={"panel " + mediumFont}>{errors.countryCode && `${errors.countryCode.message}`}</panel>
                            </FormControl>
                        )}
                    />
                    <Controller
                        control={control}
                        name="phoneNo"
                        rules={{ required: t("Phone") + t("IsRequired") }}
                        render={({ field }) => (
                            <FormControl sx={{ m: 0.5, minWidth: 100 }} error={!!errors.phoneNo}>
                                {/* <label class={"placeholder narrow " + mediumFont}>PL</label> */}
                                   <label class={"label placeholder narrow" + boldFont}>PL</label>
                                <input
                                    class={"input fontstyle narrow " + mediumFont}
                                    width={"40px; !important"}
                                    {...field}
                                    label={t("Phone")}
                                    error={!!errors.phoneNo}
                                    helperText={errors.phoneNo && `${errors.phoneNo.message}`}
                                />
                                <panel class={"panel " + mediumFont}>{errors.phoneNo && `${errors.phoneNo.message}`}</panel>
                            </FormControl>)}
                    />
                </div>
                <div class="divmargin">
                    <Controller
                        control={control}
                        name="nationality"
                        rules={{ required: t("Nationality") + t("IsRequired") }}
                        defaultValue= {defaultNationality}
                        render={({ field }) => (
                            <FormControl sx={{ m: 0.5, minWidth: 150}} error={!!errors.nationality}>
                                <label class={"label " + boldFont}>{t("Nationality")} </label>
                                <select {...field} class={"select fontstyle narrow " + mediumFont} defaultValue={defaultNationality}  >  
                                    {nationalityList.map(item => (
                                        <option value={item.Abrv2 } > {item.CountryDescription}</option >
                                    ))}
                                </select>
                                <panel class={"panel " + mediumFont}>{errors.nationality && `${errors.nationality.message}`}</panel>
                            </FormControl>
                        )}
                    /> 
                    <Controller
                        control={control}
                        name="preferLanguage"
                        rules={{ required: t("PreferLanguage") + t("IsRequired") }}
                        defaultValue= {defaultLanguage}
                        render={({ field }) => (
                            <FormControl sx={{ m: 0.5, minWidth: 150 }} error={!!errors.preferLanguage}>
                                <label class={"label " + boldFont}>{t("PreferLanguage")} </label>
                                <select {...field} class={"select fontstyle narrow " + mediumFont} defaultValue={defaultLanguage}  >  
                                    {preferLanguageList.map(item => (
                                        <option value={item.LanguageID } > {item.Description}</option >
                                    ))}
                                </select>
                                <panel class={"panel " + mediumFont}>{errors.preferLanguage && `${errors.preferLanguage.message}`}</panel>
                            </FormControl>
                        )}
                    />      
                </div>
                <div class="divmargin">
                    <Controller
                        name="email"
                        control={control}
                        // rules={{ required: t("Email") + t("IsRequired") }}
                        rules={{
                            required: t("Email") + t("IsRequired"),
                            pattern: {
                                value: /^([A-Za-z0-9_\-\.])+\@([A-Za-z0-9_\-\.])+\.([A-Za-z]{2,4})$/,
                                message: t("Email") + t("InValid")
                            }
                        }}
                        render={({ field }) => (
                            <FormControl sx={{ m: 0.5, minWidth: 320 }} error={!!errors.email}>
                                <label class={"label " + boldFont}>{t("Email")} </label>
                                <input
                                    class={"input fontstyle " + mediumFont}
                                    {...field}
                                    label={t("Email")}
                                    error={!!errors.email}
                                    helperText={errors.email && `${errors.email.message}`}
                                />
                                <panel class={"panel " + mediumFont}>{errors.email && `${errors.email.message}`}</panel>
                            </FormControl>)}
                    />
                </div>
                <div className="consent-row-wrapper">
                    <FormControl sx={{ m: 0, width: '100%' }}>
                        <div className={"consent-row " + boldFont}>
                            <input id="terms-agreement" type="checkbox" readOnly checked={agreeChecked} />
                            <div className="consent-text">
                                <label htmlFor="terms-agreement">{t("IAgree")}</label>{' '}
                                <Button className="consent-link" variant="text" onClick={handleClickOpen}
                                    aria-label={t("TCTitle")} aria-haspopup="dialog">
                                    {t("ContractLink")}
                                </Button>
                            </div>
                            <Dialog
                                open={open}
                                onClose={handleClose}
                                maxWidth="lg"
                                fullWidth
                                sx={{
                                    '& .MuiDialog-paper': {
                                        margin: { xs: '8px', sm: '32px' },
                                        maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' }
                                    }
                                }}>
                                <DialogTitle style={{
                                    textAlign: 'center',
                                    fontSize: window.innerWidth < 600 ? '18px' : '22px',
                                    fontWeight: 'bold',
                                    color: '#000',
                                    padding: '12px 16px'
                                }}>
                                    {t("TCTitle")}
                                </DialogTitle>
                                <DialogContent sx={{ padding: { xs: '8px 12px', sm: '16px 24px' } }}>
                                    <div style={{
                                        maxWidth: '100%',
                                        margin: '0 auto',
                                        fontFamily: 'Arial, sans-serif',
                                        lineHeight: '1.5'
                                    }}>
                                        <Typography style={{
                                            textAlign: 'center',
                                            fontSize: window.innerWidth < 600 ? '14px' : '18px',
                                            fontWeight: 'bold',
                                            marginBottom: '16px'
                                        }}>
                                            {t("TCSubTitle")}
                                        </Typography>

                                        {renderTermsContent()}
                                    </div>
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleDisagreeClose}>
                                        {t("DisAgree")}
                                    </Button>
                                    <Button onClick={handleClose} autoFocus>
                                        {t("Agree")}
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </div>
                    </FormControl>
                </div>
                <div className="consent-row-wrapper">
                    <div className={"consent-row " + boldFont}>
                        <input id="privacy-agreement" type="checkbox" readOnly checked={privacyChecked} />
                        <div className="consent-text">
                            <label htmlFor="privacy-agreement">{t("PrivacyPolicyAgreement")}</label>{' '}
                            <Button className="consent-link" type="button" onClick={() => setPrivacyOpen(true)}
                                aria-label={t("PrivacyPolicyTitle")} aria-haspopup="dialog">
                                {t("ContractLink")}
                            </Button>
                        </div>
                    </div>
                    <Dialog open={privacyOpen} onClose={() => setPrivacyOpen(false)}
                        aria-labelledby="privacy-policy-title" maxWidth="lg" fullWidth scroll="paper"
                        sx={{ '& .MuiDialog-paper': {
                            margin: { xs: '8px', sm: '32px' },
                            maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' }
                        } }}>
                        <DialogTitle id="privacy-policy-title" style={{
                            textAlign: 'center',
                            fontSize: window.innerWidth < 600 ? '18px' : '22px',
                            fontWeight: 'bold',
                            color: '#000',
                            padding: '12px 16px'
                        }}>
                            {t("PrivacyPolicyTitle")}
                        </DialogTitle>
                        <DialogContent sx={{ padding: { xs: '8px 12px', sm: '16px 24px' } }}>
                            <PrivacyPolicy />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => { setPrivacyChecked(false); setPrivacyOpen(false); }}>
                                {t("DisAgree")}
                            </Button>
                            <Button onClick={() => { setPrivacyChecked(true); setPrivacyOpen(false); }} autoFocus>
                                {t("Agree")}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </div>
                <div className="consent-row-wrapper">
                    <div className={"consent-row " + boldFont}>
                        <input id="declaration-agreement" type="checkbox" checked={declarationChecked}
                            onChange={event => setDeclarationChecked(event.target.checked)} />
                        <div className="consent-text">
                            <label htmlFor="declaration-agreement">{t("DeclarationConsent")}</label>{' '}
                            <Button className="consent-link" type="button" onClick={() => setDeclarationOpen(true)}
                                aria-label={t("DeclarationConsent")} aria-haspopup="dialog">
                                {t("ContractLink")}
                            </Button>
                        </div>
                    </div>
                    <Dialog open={declarationOpen} onClose={() => setDeclarationOpen(false)}
                        lang={{ en: 'en', vn: 'vi', kr: 'ko', zh: 'zh-Hant' }[language]}
                        aria-labelledby="declaration-title" maxWidth="lg" fullWidth scroll="paper"
                        sx={{ '& .MuiDialog-paper': {
                            margin: { xs: '8px', sm: '32px' },
                            maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' }
                        } }}>
                        <DialogTitle id="declaration-title" style={{
                            textAlign: 'center',
                            fontSize: window.innerWidth < 600 ? '18px' : '22px',
                            fontWeight: 'bold',
                            color: '#000',
                            padding: '12px 16px'
                        }}>
                            {t("DeclarationContentTitle")}
                        </DialogTitle>
                        <DialogContent sx={{ padding: { xs: '8px 12px', sm: '16px 24px' } }}>
                            <div style={{ fontFamily: 'Arial, sans-serif', lineHeight: 1.5 }}>
                                <Typography style={tcStyles.clause}>{t("DeclarationIntroduction")}</Typography>
                                <ul style={tcStyles.bulletList}>
                                    {t("DeclarationStatements", { returnObjects: true }).map((statement, index) => (
                                        <li key={index} style={tcStyles.bulletItem}>{statement}</li>
                                    ))}
                                </ul>
                                <Typography component="label" style={tcStyles.clause} sx={{ display: 'block' }}>
                                    {t("MarketingOptOut")}{' '}
                                    <input type="checkbox" checked={marketingOptOut}
                                        onChange={event => setMarketingOptOut(event.target.checked)} />
                                </Typography>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => { setDeclarationChecked(false); setDeclarationOpen(false); }}>
                                {t("DisAgree")}
                            </Button>
                            <Button onClick={() => { setDeclarationChecked(true); setDeclarationOpen(false); }} autoFocus>
                                {t("Agree")}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </div>
                <div class="divmargin">
                    <FormControl sx={{ m: 0.5, minWidth: 320, maxwidth: 320 }}>
                        <label class={"label-mandatory " + boldFont}> {t("Mandatory")} </label>
                    </FormControl> 
                </div>
                <div>                    
                    <FormControl sx={{ m: 0.5, minWidth: 320 }}>
                        <button class={"button " + mediumFont} type="submit" disabled={!agreeChecked || !privacyChecked || !declarationChecked}>
                            {t("Next")} </button>
                    </FormControl>
                </div>
            </div>
            </div>
        </form>
    );
}
